import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { ChangeDetectorRef, Component } from '@angular/core';
import { FormGroup, FormControl, Validators, AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { Router } from '@angular/router';
import { GlobalMessageService, GlobalMessageType } from '@spartacus/core';
import { Subscription } from 'rxjs';
import { KbExportToXlsxService } from 'src/app/services/feature/ExportCsv/kb-export-csv.service';
import { KbUpdateProfileService } from 'src/app/services/feature/update-profile/kb-update-profile.service';

@Component({
  selector: 'app-kb-my-profile',
  templateUrl: './kb-my-profile.component.html',
  styleUrls: ['./kb-my-profile.component.scss'],
  standalone: false
})
export class KbMyProfileComponent {
  isMobile: boolean = false;
  update: any;
  sub = new Subscription();
  entry: any;
  emptyString = '';
  currentPassword: boolean = false;

  constructor(private breakpointObserver: BreakpointObserver,
    private service: KbUpdateProfileService,
    private cdr: ChangeDetectorRef,
    private globalMsgService: GlobalMessageService,
    protected router: Router,
    private exportExcel: KbExportToXlsxService
  ) {
    this.sub.add(
      this.breakpointObserver
        .observe([Breakpoints.XSmall, Breakpoints.Small])
        .subscribe((result) => {
          this.isMobile = result.matches;
        }));
  }
  pwdchangeForm = new FormGroup(
    {
      titleCode: new FormControl(['', Validators.required]),
      firstName: new FormControl([{ value: '', disabled: true }, Validators.required]),
      lastName: new FormControl([{ value: '', disabled: true }, Validators.required]),
      currentPassword: new FormControl(null, [Validators.required]),
      newPassword: new FormControl(
        null,
        Validators.compose([
          Validators.required,
          Validators.minLength(12),
          this.regexTester(new RegExp("(?=.*[0-9])"), {
            requiresDigit: true
          }),
          this.regexTester(new RegExp("(?=.*[A-Z])"), {
            requiresUppercase: true
          }),
          this.regexTester(new RegExp("(?=.*[a-z])"), {
            requiresLowercase: true
          }),
          this.regexTester(new RegExp("(?=.*[()~!@#$%^&*\\-_+=`|{}\\[\\]:;\"'<>,?/])"), {
            requiresSpecialChars: true
          })
        ])
      ),
      checkNewPassword: new FormControl(null, [
        Validators.required,
        Validators.minLength(12)
      ])
    },
    {
      validators: this.compareBoth
    }
  );
  get pwdform() {
    return this.pwdchangeForm.controls;
  }

  get passwordValid() {
    return this.pwdchangeForm.controls["newPassword"].errors === null;
  }

  get requiredValid() {
    return !this.pwdchangeForm.controls["newPassword"].hasError("required");
  }

  get minLengthValid() {
    return !this.pwdchangeForm.controls["newPassword"].hasError("minlength");
  }

  get requiresDigitValid() {
    return !this.pwdchangeForm.controls["newPassword"].hasError("requiresDigit");
  }

  get requiresUppercaseValid() {
    return !this.pwdchangeForm.controls["newPassword"].hasError("requiresUppercase");
  }

  get requiresLowercaseValid() {
    return !this.pwdchangeForm.controls["newPassword"].hasError("requiresLowercase");
  }

  get requiresSpecialCharsValid() {
    return !this.pwdchangeForm.controls["newPassword"].hasError("requiresSpecialChars");
  }
  ngOnInit() {
    this.sub.add(
      this.service.getApi().subscribe((data) => {
        this.update = data;
        this.pwdchangeForm.patchValue({
          titleCode: this.update.customer.titleCode,
          firstName: this.update.customer.firstName,
          lastName: this.update.customer.lastName,
        });
        this.pwdchangeForm.get('firstName').disable();
        this.pwdchangeForm.get('lastName').disable();
        this.cdr.detectChanges();
      }));
  }
  onDropdownChange(value: any) { }

  compareBoth(control: AbstractControl) {
    const password = control.get('newPassword')?.value;
    const confirmPasswordControl = control.get('checkNewPassword');

    if (!confirmPasswordControl) {
      return null;
    }

    const confirmPassword = confirmPasswordControl.value;

    // Check if confirm password is empty
    if (!confirmPassword || confirmPassword.trim() === '') {
      confirmPasswordControl.setErrors({ required: true });
      return { required: true };
    }

    // Check min length
    if (confirmPassword.length < 12) {
      confirmPasswordControl.setErrors({ minLength: true });
      return { minLength: true };
    }

    // Check password mismatch
    if (password !== confirmPassword) {
      confirmPasswordControl.setErrors({ mismatch: true });
      return { mismatch: true };
    }

    // Clear errors if all good
    confirmPasswordControl.setErrors(null);
    return null;
  }

  regexTester(regex: RegExp, error: ValidationErrors): ValidatorFn {
    return (control: AbstractControl) => {
      if (!control.value) {
        return null;
      }

      const valid = regex.test(control.value);

      return valid ? null : error;
    };
  }
  onSubmit() {
    if (this.pwdchangeForm.invalid) {
      this.pwdchangeForm.markAllAsTouched();
    } else {
      const payload = {
        titleCode: this.pwdform.titleCode.value,
        firstName: this.pwdform.firstName.value,
        lastName: this.pwdform.lastName.value,
        currentPassword: this.pwdform.currentPassword.value,
        newPassword: this.pwdform.newPassword.value,
        checkNewPassword: this.pwdform.checkNewPassword.value
      }
      this.sub.add(
        this.service.postApi(payload).subscribe((res: any) => {
          if (res?.message) {
            const key = res.message;
            if (res.status == 'success') {
              this.currentPassword = false;
              this.formResetter();
              this.cdr.detectChanges();
            } else {
              if (res?.currentPasswordErrors) {
                this.currentPassword = true;
                this.formResetter();
              }
            }
            this.globalMsgService.add({ key: key }, GlobalMessageType.MSG_TYPE_ERROR);
          }
        }));
    }
  }
  exportUserExcel() {
    this.sub.add(this.service.getExportData().subscribe((res: any) => {
      this.exportExcel.exportwithBoldHeader(res.data, 'UserDetails.xlsx');
    }));
  }
  formResetter() {
    this.pwdform.checkNewPassword.reset();
    this.pwdform.currentPassword.reset();
    this.pwdform.newPassword.reset();
    this.pwdchangeForm.markAsUntouched();
    this.pwdchangeForm.markAsDirty();
  }
  ngOnDestroy() {
    this.sub.unsubscribe();
  }
}
